import { useEffect, useState } from 'react';
import { useAuth } from 'src/components/AuthProvider/AuthProvider';
import { useDownloadZipFile } from 'src/components/selfService/features/OrderHistory/ZipDownload/hooks/useDownloadZipFile';
import { useGenerateZipFile } from 'src/components/selfService/features/OrderHistory/ZipDownload/hooks/useGenerateZipFile';
import { useOrderReceiptsZipDetails } from 'src/components/selfService/features/OrderHistory/ZipDownload/hooks/useOrderReceiptsZipDetails';
import {
  ZipFileFilters,
  ZipFileStatus,
} from 'src/components/selfService/features/OrderHistory/ZipDownload/ZipDownload.utils';

export interface UseZipManagementProps {
  corpId: string;
  dateFrom: Date;
  dateTo: Date;
  query: string;
  totalReceipts: number;
  onDownloadSuccess: () => void;
}
export interface UseZipManagement {
  setStatus: (status: ZipFileStatus) => void;
  status: ZipFileStatus;
  maximumReceiptsAllowed: number;
  generateZipFile: () => void;
  filters: ZipFileFilters;
  getZipFileFromS3: () => void;
}

export const useZipManagement = ({
  corpId,
  dateFrom,
  dateTo,
  query,
  onDownloadSuccess,
  totalReceipts,
}: UseZipManagementProps): UseZipManagement => {
  const [status, setStatus] = useState<ZipFileStatus>(ZipFileStatus.IDLE);
  const [filters, setFilters] = useState<ZipFileFilters>(getFilters());
  const { user } = useAuth();

  const {
    generateZipFile,
    status: generateStatus,
    downloadUrl,
  } = useGenerateZipFile({
    corpId,
    dinerId: user.ud_id,
    ...filters,
  });

  const {
    maximumReceiptsAllowed,
    filters: filtersFromDetails,
    status: statusFromDetails,
  } = useOrderReceiptsZipDetails({
    corpId,
  });

  const { getZipFileFromS3 } = useDownloadZipFile({
    downloadUrl,
    onDownload: successDownloadFileHandle,
    onError: errorDownloadFileHandle,
  });

  useEffect(() => {
    if (statusFromDetails === ZipFileStatus.FETCHING) {
      generateZipFile();
      setFilters(filtersFromDetails);
    }
    setStatus(statusFromDetails);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [statusFromDetails, filtersFromDetails]);

  useEffect(() => {
    if (generateStatus === ZipFileStatus.COMPLETE) {
      getZipFileFromS3();
    }
    setStatus(generateStatus);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [generateStatus]);

  useEffect(() => {
    if (
      status === ZipFileStatus.GENERATE ||
      status === ZipFileStatus.TOO_MANY
    ) {
      setFilters(getFilters());
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [status, dateTo, dateFrom, query]);

  useEffect(() => {
    const isTooMany = totalReceipts > maximumReceiptsAllowed;

    if (status === ZipFileStatus.GENERATE && isTooMany) {
      setStatus(ZipFileStatus.TOO_MANY);
    }
    if (status === ZipFileStatus.TOO_MANY && !isTooMany) {
      setStatus(ZipFileStatus.GENERATE);
    }
  }, [status, totalReceipts, maximumReceiptsAllowed]);

  function successDownloadFileHandle() {
    setStatus(ZipFileStatus.DOWNLOAD_SUCCESSFULLY);
    onDownloadSuccess();
  }

  function errorDownloadFileHandle() {
    setStatus(ZipFileStatus.DOWNLOAD_FAILED);
  }

  function getFilters(): ZipFileFilters {
    return {
      dateFrom: dateFrom && dateFrom.toISOString(),
      dateTo: dateTo && dateTo.toISOString(),
      ...(!!query && { query }),
    };
  }

  return {
    setStatus,
    status,
    maximumReceiptsAllowed,
    generateZipFile,
    filters,
    getZipFileFromS3,
  };
};
